import { action, computed, observable } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { type Id } from '@/infra/id'
import { of, type Observable } from 'rxjs'
import { asAtom } from '@/utils/mobx-util'

export type UserProps = ModelProps<User>

/**
 * ID of the system robot.
 */
export const SYSTEM_ROBOT_PUBLIC_ID = '0775cd0d-3438-4b2e-aa1a-cf3dace76a70'

/**
 * Represents a user on the platform.
 */
export class User extends Model<User> {
  /**
   * Placeholder instance.
   */
  static readonly none = new User(() => false, of(false))

  /**
   * Whether the user is online.
   * The use of `asAtom` here ensures we'll only be syncing online status
   * while there are observers on the {@link isOnline} property.
   */
  private readonly _isOnline

  /**
   * The (public) ID of the user.
   */
  id: Id

  /**
   * The ID of the user based on the authentication provider.
   */
  authId: Id

  /**
   * The internal ID of the user.
   * Should only be used for signing up.
   */
  internalId: Id

  @observable accessor givenName = ''
  @observable accessor familyName = ''
  @observable accessor avatarUrl: string | null = null

  // NOTE: These will only be populated for the current user
  // or if the caller is a platform admin.
  @observable accessor email: string | null = null
  @observable accessor phone: string | null = null
  @observable accessor platformAdmin = false

  /** @see mfaEnabled */
  @observable private accessor _mfaEnabled = false

  /** @see isFullyLoaded */
  @observable private accessor _isFullyLoaded = false

  constructor(
    private readonly _isCurrentUser: (user: User) => boolean,
    isOnline$: Observable<boolean>,
  ) {
    super()
    this._isOnline = asAtom(isOnline$, false)
  }

  /**
   * Whether this user represents the System Robot.
   */
  @computed
  get isSystemRobot() {
    return this.id === SYSTEM_ROBOT_PUBLIC_ID
  }

  /**
   * Whether the user is currently online.
   */
  @computed get isOnline() {
    return this._isOnline()
  }

  /**
   * Whether this is the current user.
   */
  @computed get isCurrentUser() {
    return this._isCurrentUser(this)
  }

  /**
   * Tracks whether MFA is enabled.
   */
  @computed get mfaEnabled() {
    return this._mfaEnabled
  }

  /**
   * Tracks whether the User has been fully loaded with data from IAM.
   */
  @computed get isFullyLoaded() {
    return this._isFullyLoaded
  }

  /**
   * Gets the formatted name for the current user.
   */
  @computed get displayName() {
    return `${this.givenName} ${this.familyName}`.trim() || 'User'
  }

  /**
   * Marks multi-factor authentication as enabled.
   */
  @action.bound
  markMultiFactorAuthEnabled() {
    this._mfaEnabled = true
  }

  /**
   * Marks the instance as having been fully loaded.
   */
  @action.bound
  markFullyLoaded() {
    this._isFullyLoaded = true
  }
}
